import React, { useCallback, useEffect } from 'react';
import SearchComboBox from '../../Search/components/SearchComboBox';
import SearchDropdown from '../../Search/components/SearchDropDown';
import { FacetItem, SearchFacet } from '../../../types/search';
import { Label, Button, DateRange, DateValue } from 'react-aria-components';
import { useDateFilter } from '../contexts/PortalDateFilterProvider';
import { parseDate, DateFormatter } from '@internationalized/date';

type PortalFilterWrapperProps = Readonly<{
  facetGroups: SearchFacet[];
  appliedFilters: SearchFacet[];
  pendingFilterState: Record<string, string | string[]> | null;
  handleFilterChange: (filter: string, values: string | string[]) => void;
  setFilterDatePickerOpen: (isOpen: boolean) => void;
  setFiltersHeading: (heading: string) => void;
  setCurrentDateFacet: (facet: SearchFacet | null) => void;
  sortByOptions: { key: string; label: string; options?: Record<string, string> }[];
  sortOrder?: string;
  sortCol?: string;
  setCurrentSortCol: (sortCol: string | undefined) => void;
  setCurrentSortOrder: (sortOrder: string | undefined) => void;
  labels?: { sortByLabel?: string };
}>;

function parseDateRange(dateString: string | string[]): DateRange | null {
  try {
    if (!dateString) {
      return null;
    }

    if (Array.isArray(dateString)) {
      if (dateString.length === 1) {
        // Single date - use same date for start and end
        return {
          start: parseDate(dateString[0]),
          end: parseDate(dateString[0]),
        };
      } else if (dateString.length >= 2) {
        return {
          start: parseDate(dateString[0]),
          end: parseDate(dateString[1]),
        };
      }
      return null;
    }

    // Handle legacy comma-separated string format
    const dates = dateString.split(',');
    if (dates.length === 1) {
      // Single date - use same date for start and end
      return {
        start: parseDate(dates[0]),
        end: parseDate(dates[0]),
      };
    } else if (dates.length >= 2) {
      return {
        start: parseDate(dates[0]),
        end: parseDate(dates[1]),
      };
    }

    return null;
  } catch (e) {
    console.error('Error parsing date range value:', e);
    return null;
  }
}

function getDateFromURL(facetKey: string): string[] | null {
  const urlParams = new URLSearchParams(globalThis.location.search);
  const value = urlParams.get(facetKey);

  if (!value) return null;

  // Handle legacy comma-separated format from URL
  if (value.includes(',')) {
    const dates = value.split(',');
    return dates.length >= 2 ? dates : null;
  }

  // Single date should be wrapped in an array
  return [value];
}

export default function PortalFilterWrapper({
  facetGroups,
  appliedFilters,
  pendingFilterState,
  handleFilterChange,
  setFilterDatePickerOpen,
  setFiltersHeading,
  setCurrentDateFacet,
  sortByOptions,
  sortOrder,
  sortCol,
  setCurrentSortCol,
  setCurrentSortOrder,
  labels,
}: PortalFilterWrapperProps) {
  return (
    <>
      <SortByDropdown
        sortByOptions={sortByOptions}
        sortOrder={sortOrder}
        sortCol={sortCol}
        setCurrentSortCol={setCurrentSortCol}
        setCurrentSortOrder={setCurrentSortOrder}
        labels={labels}
      />
      {facetGroups?.map((facet: SearchFacet) => {
        if (facet.type === 'dropdown') {
          return (
            <FacetDropdown
              key={facet.key}
              facet={facet}
              pendingFilterState={pendingFilterState}
              appliedFilters={appliedFilters}
              handleFilterChange={handleFilterChange}
            />
          );
        } else if (facet.type === 'date') {
          return (
            <FacetDate
              key={facet.key}
              facet={facet}
              pendingFilterState={pendingFilterState}
              appliedFilters={appliedFilters}
              setFilterDatePickerOpen={setFilterDatePickerOpen}
              setFiltersHeading={setFiltersHeading}
              setCurrentDateFacet={setCurrentDateFacet}
            />
          );
        }
      })}
    </>
  );
}

type SortByDropdownProps = Readonly<{
  sortByOptions: { key: string; label: string; options?: Record<string, string> }[];
  sortOrder?: string;
  sortCol?: string;
  setCurrentSortCol: (sortCol: string | undefined) => void;
  setCurrentSortOrder: (sortOrder: string | undefined) => void;
  labels?: { sortByLabel?: string };
}>;
function SortByDropdown({
  sortByOptions,
  sortOrder,
  sortCol,
  setCurrentSortCol,
  setCurrentSortOrder,
  labels,
}: SortByDropdownProps) {
  const items: { key: string; label: string; sortColKey?: string }[] = [];

  for (const sortOption of sortByOptions) {
    if (sortOption.options) {
      const camelCasedKey = sortOption.key.charAt(0).toLowerCase() + sortOption.key.slice(1);

      Object.entries(sortOption.options).forEach(([key, label]) => {
        items.push({ key: camelCasedKey + '-' + key, label });
      });
    }
  }

  function handleOnSortChange(value: string) {
    const [newSortCol, newSortOrder] = value.split('-');
    setCurrentSortCol(newSortCol || undefined);
    setCurrentSortOrder(newSortOrder || undefined);
  }

  // Don't render if no items
  if (items.length === 0) {
    return null;
  }

  return (
    <SearchDropdown
      filterLabel={labels?.sortByLabel || 'Sort by'}
      items={items}
      selectedKey={`${sortCol}-${sortOrder}`}
      onChange={handleOnSortChange}
    />
  );
}

function formatDate(date: DateValue) {
  const formatter = new DateFormatter('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return formatter.format(date.toDate('UTC'));
}

function FacetDate({
  facet,
  pendingFilterState,
  appliedFilters,
  setFilterDatePickerOpen,
  setFiltersHeading,
  setCurrentDateFacet,
}: Readonly<{
  facet: SearchFacet;
  pendingFilterState: Record<string, string | string[]> | null;
  appliedFilters: SearchFacet[];
  setFilterDatePickerOpen: (isOpen: boolean) => void;
  setFiltersHeading: (heading: string) => void;
  setCurrentDateFacet: (facet: SearchFacet | null) => void;
}>) {
  const { selectedValue, setSelectedValue, selectedValueLabel, setSelectedValueLabel } = useDateFilter(facet.key);

  const getPendingValue = (facetKey: string): string[] | null => {
    const pendingValue = pendingFilterState?.[facetKey];
    if (!pendingValue) return null;
    return typeof pendingValue === 'string' ? [pendingValue] : pendingValue;
  };

  const getAppliedFilterValue = (facetKey: string): string[] | null => {
    const appliedFilter = appliedFilters.find((filter: SearchFacet) => filter.key === facetKey);
    if (!appliedFilter) return null;

    if (appliedFilter.items && appliedFilter.items.length > 0) {
      return appliedFilter.items.map(item => item.value);
    }

    return getDateFromURL(facetKey);
  };

  const getContextValue = (): string[] | null => {
    if (selectedValue && selectedValue !== '') {
      return selectedValue.split(',');
    }
    return null;
  };

  const dateRangeValue = React.useMemo<string | string[] | null>(() => {
    // Priority 1: Pending filter state
    const pendingValue = getPendingValue(facet.key);
    if (pendingValue) return pendingValue;

    // Priority 2: Applied filter or URL value
    const appliedValue = getAppliedFilterValue(facet.key);
    if (appliedValue) return appliedValue;

    // Priority 3: Context value
    return getContextValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFilterState, facet.key, selectedValue, appliedFilters]);

  const makeLabelFromDateRange = useCallback((dateString: string | string[]): string => {
    const dateValue = parseDateRange(dateString);
    if (!dateValue) return 'Any';

    // If start and end are the same, show single date
    if (dateValue.start.compare(dateValue.end) === 0) {
      return formatDate(dateValue.start);
    }

    return formatDate(dateValue.start) + ' - ' + formatDate(dateValue.end);
  }, []);

  // Sync dateRangeValue with selectedValue in context
  useEffect(() => {
    if (dateRangeValue !== null) {
      // Convert array to string for comparison and storage
      const valueToStore = Array.isArray(dateRangeValue) ? dateRangeValue.join(',') : dateRangeValue;

      // Only update if the stored value is different
      if (valueToStore !== selectedValue) {
        const newLabel = makeLabelFromDateRange(dateRangeValue);
        setSelectedValue(valueToStore);
        setSelectedValueLabel(newLabel);
      }
    } else if (selectedValue !== '' || selectedValueLabel !== 'Any') {
      // Reset to default when no date is selected
      setSelectedValue('');
      setSelectedValueLabel('Any');
    }
  }, [
    dateRangeValue,
    selectedValue,
    selectedValueLabel,
    setSelectedValue,
    setSelectedValueLabel,
    makeLabelFromDateRange,
  ]);

  function openDatePicker() {
    setCurrentDateFacet(facet);
    // Don't reset selectedValue - keep it synced from pendingFilterState/URL
    // so the date picker can highlight the previously selected dates
    setFilterDatePickerOpen(true);
    setFiltersHeading(facet.heading);
  }

  return (
    <div className="search-date">
      <Label className="search-date__label">{facet.heading}</Label>
      <Button
        className="search-date__open-date-btn"
        type="button"
        onClick={openDatePicker}
        aria-label={`Select dates for ${facet.heading}`}
      >
        <span className="search-date__open-date-btn-content">
          <svg className="search-date__open-date-calendar-icon icon" aria-hidden="true">
            <use href="/dist/assets/icons/sprite.svg#calendar"></use>
          </svg>
          <span className="search-date__open-date-btn-text">{selectedValueLabel}</span>
        </span>
        <svg className="search-date__open-date-arrow-icon icon" aria-hidden="true">
          <use href="/dist/assets/icons/sprite.svg#nav-arrow-right"></use>
        </svg>
      </Button>
    </div>
  );
}

function FacetDropdown({
  facet,
  pendingFilterState,
  appliedFilters,
  handleFilterChange,
}: Readonly<{
  facet: SearchFacet;
  pendingFilterState: Record<string, string | string[]> | null;
  appliedFilters: SearchFacet[];
  handleFilterChange: (filter: string, values: string | string[]) => void;
}>) {
  const { key, heading, items } = facet;

  let selectedKey: string | null;

  if (pendingFilterState?.[key]) {
    // Use pending selection if it exists
    const pendingValue = pendingFilterState[key];
    selectedKey = typeof pendingValue === 'string' ? pendingValue : pendingValue[0];
  } else {
    // Fall back to applied filter
    const appliedFilter = appliedFilters.find((appliedFilter: SearchFacet) => appliedFilter.key === key);
    const appliedFilterItems = appliedFilter?.items.filter((item: FacetItem) => item.isSelected);
    selectedKey = appliedFilterItems && appliedFilterItems.length > 0 ? appliedFilterItems[0].value : null;
  }

  return (
    <SearchComboBox
      key={key}
      facetKey={key}
      facetHeading={heading}
      items={items}
      onChange={handleFilterChange}
      selectedKey={selectedKey}
    />
  );
}
